import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import SEOSettingsBlock from './SEOSettingsBlock';
import ImageUploader from './ImageUploader';

/**
 * Global SEO & Sharing Settings.
 *
 * Persists into the existing `SiteSettings` document (seo_title,
 * seo_description, seo_og_image). Applied as the default meta tags for the
 * homepage and any page that does not have its own page-level SEO values.
 */
const SEOSettingsManager = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        setForm({
          seo_title: data.seo_title || '',
          seo_description: data.seo_description || '',
          seo_og_image: data.seo_og_image || '',
          seo_no_index: !!data.seo_no_index,
          seo_no_follow: !!data.seo_no_follow,
          favicon_url: data.favicon_url || '',
          blog_list_seo_title: data.blog_list_seo_title || '',
          blog_list_seo_description: data.blog_list_seo_description || '',
          blog_list_seo_og_image: data.blog_list_seo_og_image || '',
          blog_list_seo_no_index: !!data.blog_list_seo_no_index,
          blog_list_seo_no_follow: !!data.blog_list_seo_no_follow,
          _all: data,
        });
      } catch (e) {
        setStatus('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setStatus('');
    try {
      const merged = {
        ...form._all,
        seo_title: form.seo_title || '',
        seo_description: form.seo_description || '',
        seo_og_image: form.seo_og_image || '',
        seo_no_index: !!form.seo_no_index,
        seo_no_follow: !!form.seo_no_follow,
        favicon_url: form.favicon_url || '',
        blog_list_seo_title: form.blog_list_seo_title || '',
        blog_list_seo_description: form.blog_list_seo_description || '',
        blog_list_seo_og_image: form.blog_list_seo_og_image || '',
        blog_list_seo_no_index: !!form.blog_list_seo_no_index,
        blog_list_seo_no_follow: !!form.blog_list_seo_no_follow,
      };
      const { data } = await axios.put(`${API}/admin/site-settings`, merged, { headers: adminAuthHeader() });
      setForm({
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
        seo_og_image: data.seo_og_image || '',
        seo_no_index: !!data.seo_no_index,
        seo_no_follow: !!data.seo_no_follow,
        favicon_url: data.favicon_url || '',
        blog_list_seo_title: data.blog_list_seo_title || '',
        blog_list_seo_description: data.blog_list_seo_description || '',
        blog_list_seo_og_image: data.blog_list_seo_og_image || '',
        blog_list_seo_no_index: !!data.blog_list_seo_no_index,
        blog_list_seo_no_follow: !!data.blog_list_seo_no_follow,
        _all: data,
      });
      setStatus('Saved');
      setTimeout(() => setStatus(''), 2200);
    } catch (e) {
      setStatus('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-500 p-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  if (!form) return null;

  return (
    <div className="max-w-3xl" data-testid="global-seo-manager">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SEO &amp; Sharing Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Defaults for the homepage and any page that doesn&apos;t have its own SEO values.</p>
      </div>
      <SEOSettingsBlock
        values={form}
        onChange={upd}
        heading="Global Defaults"
        subheading="Applies to the homepage and every page that has not set its own SEO overrides."
        defaultOpen
        testIdPrefix="global-seo"
      />

      {/* Blog Listing (/blog) — dedicated SEO block, same fields as any page */}
      <div className="mt-4">
        <SEOSettingsBlock
          values={{
            seo_title: form.blog_list_seo_title,
            seo_description: form.blog_list_seo_description,
            seo_og_image: form.blog_list_seo_og_image,
            seo_no_index: form.blog_list_seo_no_index,
            seo_no_follow: form.blog_list_seo_no_follow,
          }}
          onChange={(k, v) => upd(`blog_list_${k}`, v)}
          heading="Blog Listing (/blog)"
          subheading="Metadata for the public Blog listing page. Leave empty to fall back to the global defaults."
          testIdPrefix="blog-list-seo"
        />
      </div>

      {/* Favicon uploader — persists into site_settings.favicon_url and is
          applied to every page at runtime by SettingsProvider. */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4" data-testid="favicon-block">
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-widest">Favicon</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Small icon shown in browser tabs & bookmarks. Recommended <strong>32×32px</strong> or <strong>64×64px</strong>, <span className="font-mono">.ico</span> or <span className="font-mono">.png</span>. Applies to every page immediately after Save.</p>
        </div>
        <ImageUploader
          value={form.favicon_url || ''}
          onChange={(url) => upd('favicon_url', url)}
          label="Favicon"
          hint="Square PNG or ICO; keep under 100 KB for fast load"
          height="h-24"
        />
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-60"
          data-testid="global-seo-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        {status && <span className={`text-xs ${status === 'Saved' ? 'text-green-600' : 'text-red-600'}`} data-testid="global-seo-status">{status}</span>}
      </div>
    </div>
  );
};

export default SEOSettingsManager;
